import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import ReadingProgress from './components/ReadingProgress';
import ArticleHeader from './components/ArticleHeader';
import ArticleBody from './components/ArticleBody';
import ShareBar from './components/ShareBar';
import {
  getArticleById,
  getAdjacentArticles,
  getHotArticles,
  getRelatedArticles,
  getCategoryName,
} from '@/utils/content';

export default function ArticlePage() {
  const { id = '' } = useParams();
  const article = getArticleById(id);

  if (!article) {
    return (
      <SiteLayout>
        <Seo
          title="内容不存在｜深夜爆料"
          description="你访问的爆料内容不存在或已被调整，返回首页查看深夜爆料的最新首发内容。"
          path={`/article/${id}`}
        />
        <div className="mx-auto w-full px-4 py-24 text-center md:px-10">
          <i className="ri-file-unknow-line text-5xl text-background-400"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-50">这条爆料找不到了</h1>
          <p className="mt-3 text-sm text-foreground-500">链接可能已经过期，或者内容做了调整。</p>
          <Link
            to="/"
            className="mt-7 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
          >
            <i className="ri-arrow-left-line"></i>
            回到首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedArticles(article, 3);
  const hotList = getHotArticles(4).filter((item) => item.id !== article.id);
  const { prev, next } = getAdjacentArticles(article);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.date,
      dateModified: article.date,
      articleSection: getCategoryName(article.categorySlug),
      author: { '@type': 'Organization', name: article.author },
      publisher: { '@type': 'Organization', name: '深夜爆料' },
      keywords: article.tags.join(','),
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
        {
          '@type': 'ListItem',
          position: 2,
          name: getCategoryName(article.categorySlug),
          item: `/category/${article.categorySlug}`,
        },
        { '@type': 'ListItem', position: 3, name: article.title, item: `/article/${article.id}` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <ReadingProgress />
      <Seo
        title={`${article.title}｜深夜爆料`}
        description={article.excerpt}
        keywords={`深夜爆料,${getCategoryName(article.categorySlug)},${article.tags.join(',')}`}
        path={`/article/${article.id}`}
        ogType="article"
        ogImage={article.cover}
        jsonLd={jsonLd}
      />

      <div className="mx-auto w-full px-4 py-8 md:px-10 md:py-12">
        <Breadcrumbs
          items={[
            { label: getCategoryName(article.categorySlug), path: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.9fr_1fr] lg:gap-14">
          <article>
            <ArticleHeader article={article} />
            <ArticleBody article={article} />
            <ShareBar title={article.title} />

            <nav className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" aria-label="上一篇与下一篇">
              {prev ? (
                <Link
                  to={`/article/${prev.id}`}
                  className="group rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-500/40"
                >
                  <span className="flex items-center gap-1.5 text-xs text-foreground-600">
                    <i className="ri-arrow-left-line"></i>
                    上一条
                  </span>
                  <span className="mt-2 line-clamp-2 block text-sm text-foreground-200 transition-colors group-hover:text-primary-300">
                    {prev.title}
                  </span>
                </Link>
              ) : (
                <div className="hidden sm:block"></div>
              )}
              {next ? (
                <Link
                  to={`/article/${next.id}`}
                  className="group rounded-lg border border-background-200 bg-background-100 p-4 text-right transition-colors hover:border-primary-500/40"
                >
                  <span className="flex items-center justify-end gap-1.5 text-xs text-foreground-600">
                    下一条
                    <i className="ri-arrow-right-line"></i>
                  </span>
                  <span className="mt-2 line-clamp-2 block text-sm text-foreground-200 transition-colors group-hover:text-primary-300">
                    {next.title}
                  </span>
                </Link>
              ) : null}
            </nav>
          </article>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-100">
                <i className="ri-fire-line text-accent-400"></i>
                此刻最热
              </h2>
              <div className="mt-3 flex flex-col gap-1">
                {hotList.map((item, index) => (
                  <div key={item.id} className="flex gap-3 border-b border-background-200/70 pb-1 last:border-0">
                    <span className="font-heading text-xl font-bold leading-none text-background-400">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="min-w-0 flex-1">
                      <ArticleCard article={item} variant="compact" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-100">
                <i className="ri-compass-3-line text-primary-400"></i>
                栏目导航
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="rounded-full bg-primary-500/15 px-3 py-1.5 text-xs text-primary-300 transition-colors hover:bg-primary-500/25"
                >
                  更多{getCategoryName(article.categorySlug)}
                </Link>
                <Link
                  to="/"
                  className="rounded-full bg-background-200 px-3 py-1.5 text-xs text-foreground-300 transition-colors hover:bg-background-300"
                >
                  回首页看爆料流
                </Link>
                <Link
                  to="/contact"
                  className="rounded-full bg-background-200 px-3 py-1.5 text-xs text-foreground-300 transition-colors hover:bg-background-300"
                >
                  我要投稿
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-16 border-t border-background-200/70 pt-12">
          <h2 className="mb-7 font-heading text-2xl font-bold text-foreground-50">
            读完这条，你还会想看
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <ArticleCard key={item.id} article={item} variant="standard" />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}